// Set of functions that can be used globally.
// Use by:
//   import * as Common from './Common.js';
//   ...
//   Then call whatever function you want:
//   Common.trimmedEventString(event, "name"); 



// import dayjsPkg from 'dayjs';
// const {dayjs} = dayjsPkg;
// const {utc} = utcPkg;
// import { customParseFormat } from 'dayjs/plugin/customParseFormat.js';
// const { customParseFormat } = pkg;

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc.js';
import customParseFormat from 'dayjs/plugin/customParseFormat.js';
import localizedFormat from 'dayjs/plugin/localizedFormat.js';
dayjs.extend(utc);
dayjs.extend(customParseFormat);
dayjs.extend(localizedFormat);

// Javascript min/max dates:
export let maxDate = new Date(8640000000000000);
export let minDate = new Date(-8640000000000000);

// Trims a string (or null).  If empty, returns null -- otherwise the trimmed value.
export function trimmedStringOrNull(value) {
  if (null == value) {
    return null;
  }
  const trimmed = value.trim();
  if (0 === trimmed.length) {
    return null;
  }
  return trimmed;
}

// Pulls a value out of an event, trims it.  If empty, returns null -- otherwise the trimmed value.
export function trimmedEventString(event, name) {
  const value = event.target[name].value;
  return trimmedStringOrNull(value);
}

// Takes a string, trims it.  Returns the trimmed string converted to a float,
// null if empty or null string.  Throws errorMessage, if not empty and not a float.
export function trimmedFloatOrNull(floatString, errorMessage) {
  if ((null == floatString) || (0 === floatString.length)) {
    return null;
  }

  var floatRegex = /^-?\d+(?:[.,]\d*?)?$/;
  if (!floatRegex.test(floatString)) {
    throw new Error(errorMessage);
  }

  const parsed = parseFloat(floatString);
  if (isNaN(parsed)) {
    throw new Error(errorMessage);
  }

  return parsed;
}


// Pulls a float out of an event, trims it.  Returns the trimmed string converted to a float,
// null if empty string.  Throws errorMessage, if not empty and not a float.
export function trimmedEventFloat(event, name, errorMessage) {
  const value = trimmedEventString(event, name);
  return trimmedFloatOrNull(value, errorMessage);
}


// In the DB, dates are stored in an ISO 8601 Date Time string ("YYYY-MM-DDThh:mm:ss.sssZ").
// The DatePicker control takes a Javascript Date object, translate to/from that.
export function dateStringToDate(dateString) {
  if ((null === dateString) || ("" === dateString) || (undefined === dateString)) {
    return null;
  }

  // I moved to date/time in database instead of just date, so now do this:
  return dayjs(dateString).toDate();

  // Dates are stored in the DB as YYYY-MM-DDThh:mm:ss.sssZ. If I pass that to new Date(), it
  // converts to local time zone and the date can be wrong (i.e. midnight 3/20/21 UTC
  // is 3/19/21 in Pacific timezone). dayjs makes the conversion correctly for me.
  // return dayjs(dateString, "YYYY-MM-DD").toDate();
}


// I use AWSDateTime in the database. These are ISO 8601 date and time formate (YYYY-MM-DDThh:mm:ss.sssZ),
// optionally with timezone offset ('YYYY-MM-DDThh:mm:ss.sss-07:00'.
export function databaseDateTimeString(date) {
  return dayjs(date).format();
}


// The shape a stored AWSDateTime has: a date, a time, and an OFFSET that is not
// optional. `YYYY-MM-DD` `T` `hh:mm:ss[.sss]` `Z | ±hh:mm`.
//
// One regex, two readers, and that is the point. TaskRecurrence's nextDueDate
// `exec`s it for the capture groups so it can roll the date part and re-emit
// with the offset it was handed, and every writer checks a value against it
// before storing one. Two spellings of this rule would be free to disagree
// about what counts as storable, which is how a bare `2026-01-31` reaches a GSI
// sort key: AppSync rejects it against the AWSDateTime scalar, and if it did
// not, CLAUDE.md's cross-cutting rule says a date-only or `Z` spelling does not
// sort against a local-offset one.
export const DATABASE_DATE_TIME =
  /^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2}:\d{2}(?:\.\d+)?)(Z|[+-]\d{2}:\d{2})$/;


// Whether `value` is already in the stored spelling. A `<input type="date">`
// yields `2026-01-31`, which is NOT -- see DATABASE_DATE_TIME.
export function isDatabaseDateTimeString(value) {
  return typeof value === 'string' && DATABASE_DATE_TIME.test(value);
}


// Add a number of days to a date.
export function datePlusDays(date, days) {
  let day = dayjs(date);
  let ret = day.add(days, 'day');
  return ret.toDate();
}


// Add a number of calendar months to a date, keeping the same day-of-month
// where possible. dayjs clamps to the last valid day for shorter months
// (e.g. Jan 31 + 1 month -> Feb 28/29), which gives "same day each month"
// recurrence semantics for monthly / quarterly / semi-annual / annual tasks.
export function datePlusMonths(date, months) {
  let day = dayjs(date);
  let ret = day.add(months, 'month');
  return ret.toDate();
}


// Date to human readable string. Date only, no time of day (e.g. "Jul 2, 2026").
export function displayDate(date) {
  if (null === date) {
    return '-';
  }
  return dayjs(date).format('ll');
}

// Date for shorter human readable string.
export function shortDisplayDate(date) {
  if (null === date) {
    return '-';
  }
  return dayjs(date).format('D-MMM-YY');
}

// Date for a schedule: weekday first, then a month-name date (e.g. "Sun Aug 30").
// Which day of the week a trip leg falls on is what a reader is actually
// checking in a list of them, and a named month is what they check it against
// -- "08-30" reads as data, "Aug 30" reads as a day.
//
// The year appears only when it is NOT the current one ("Mon Jan 4 '27"). A
// trip's legs are almost always this year's, and every surface that lists them
// prints the trip's full date range with the year directly above the table
// (tripGroupDateRange), so carrying it on every row would spend the width this
// spelling exists to give back. On a phone that width is the difference
// between a schedule and a sideways scroll.
export function weekdayShortDate(date) {
  if (null === date || undefined === date) {
    return '-';
  }
  const day = dayjs(date);
  return day.year() === dayjs().year()
    ? day.format('ddd MMM D')
    : day.format("ddd MMM D [']YY");
}


// Finds the given itemCounter in an item by the counter's ID.
// Returns null if not found.
export function itemCounterFromId(item, id) {
  if (!item || !item.itemCounter || !item.itemCounter.items.length) {
    console.error('No item counters?');
    return null;
  }

  for (var i=0; i < item.itemCounter.items.length; i++) {
    if (item.itemCounter.items[i].id === id) {
      return item.itemCounter.items[i];
    }
  }

  console.error(`No counter found for ${id}?`);
  return null;
}


// Given an Item and and itemCounterId, return the itemCounterId.name.
// Null if cannot find.
export function counterName(item, itemCounterId) {
  if (!itemCounterId) {
    return '';
  }
  let counter = itemCounterFromId(item, itemCounterId);

  if (counter) {
    return counter.name;
  }

  return null;
}


// One of the task fields is the "Counter Based On".
// This is a "select" which lets the user pick from
// a list. The list is built at runtime with the
// item's counterArray.
export function buildFieldsForItem(fieldsWithoutSelect, item) {
  let fields = fieldsWithoutSelect;

  for (var i=0; i < fields.length; i++) {
    // Map the counter ID's to names.
    if ("itemCounterId" === fields[i].key) {
      let field = fields[i];

      field.selects = item.itemCounter.items.map( (counterItem, index) => {
        // index + 1 to make room for no-counter, set below.
        return {"id" : counterItem.id, "index" : index+1, "name" : counterItem.name }
      });

      // Allow selecting no-counter.
      field.selects.unshift({"id" : null, "index": 0, "name" : "(None)"})
      fields[i] = field;

      // Just the one to do...
      break;
    }
  }

  return fields;
}


// When writing to the database, an object needs to contain just the fields the
// database know about. I sometimes add extra fields (like taskBucket in a task).
// This copies an object and only adds the known fields, returning that item.
//
// A key the caller actually set is carried through whatever its value --
// including null, 0, false and "". AppSync treats null and "omitted"
// differently on a partial update: only an explicit null clears the stored
// value, so dropping falsy values here (as this used to) meant a field the
// user emptied silently kept its old value. A key that isn't on the object at
// all (or is undefined) still stays out, leaving the server's value alone.
//
// Two things callers must handle themselves, because this function can't know
// about them: a field the schema marks required must never arrive as null
// (`updateTask` / `saveEditedItem` check before calling), and an optional GSI
// key must be omitted rather than nulled (see `withoutNullIndexKeys` in
// Database.mjs).
export function copyOnlyKnownFields(item, fields) {
  let output = {};
  for (var i=0; i < fields.length; i++) {
    const field = fields[i];

    // Interval fields back two underlying columns (days + months) but only
    // one is ever set at a time. Always carry both (including null) so that
    // switching the unit -- e.g. from "30 days" to "3 months" -- clears the
    // now-unused column on the server instead of leaving a stale value that
    // would make the task look like it recurs on both schedules.
    if (field.type === 'interval') {
      output[field.key] = (item[field.key] != null) ? item[field.key] : null;
      if (field.monthsKey) {
        output[field.monthsKey] = (item[field.monthsKey] != null) ? item[field.monthsKey] : null;
      }
      continue;
    }

    if (item[field.key] !== undefined) {
      output[field.key] = item[field.key];
    }
  }
  return output;
}

// Does a registry field hold something worth displaying? A number counts --
// including 0 -- which is the point: the item info card and the status digest
// used to test `value.length > 0`, which no number has, so Counter Installed
// never rendered anywhere. Null, undefined, NaN and a blank string do not.
export function hasFieldValue(value) {
  if (value === null || value === undefined) return false;
  if (typeof value === 'number') return Number.isFinite(value);
  if (typeof value === 'string') return value.trim().length > 0;
  return true;
}


// Translate errors to text.
// A fresh random id, for the unguessable keys the app mints client-side
// (unsubscribe keys, trip ids).
//
// jsdom 16.7 -- what CRA 5's Jest runs -- has no `crypto` global at all, so
// `crypto.randomUUID()` cannot be called unguarded or every test that mints one
// throws. Node 22 and every browser the app supports have it; the fallback is
// exercised only by the test environment.
//
// TripIdentity.newTripGroupId predates this and keeps its own copy of the guard
// (with its own prefix); don't add a third.
export function randomId(prefix = '') {
  const c = globalThis.crypto;
  if (c && typeof c.randomUUID === 'function') return `${prefix}${c.randomUUID()}`;
  return `${prefix}${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

export function extractErrorText(err) {
  var errString = '';
  if (!err) {
      errString = '?';
  } else if (err.errors) {
      for (var i = 0; i < err.errors.length; i++) {
          if (i > 0) {
              errString = errString + ', ';
          }
          errString = errString + (err.errors[i].message || "");
      }
  } else {
      errString = err.toString();
  }
  return errString;
}


